//@ts-check
import Maybe from 'data.maybe';
import _ from 'lodash';
import {ifElse, pipe, head, prop, last, tap, reduce, maxBy, pathOr} from 'ramda';
import {Collection} from 'backbone';
import {propEq, bindAllMethods} from '../../backboneHelpers';

import {getFileData, uploadFileWithData, pullFilesFromInput} from '@sinclair-digital/v2-content-service/lib/file';
import {collectionMixins} from '../../sinclair/sinclair.mixins';
import MediaModel, {captionFilter} from '../../models/media/mediaModel';
import ImageModel from '../../models/media/imageModel2';
import VideoModel from '../../models/media/videoModel2';
import {createImmutableCopy} from '../../sinclair/sinclair.utils';

const {Just, Nothing, fromNullable} = Maybe;

// mediaOrModelToMedia :: [Media] | Package -> Media
const mediaOrModelToMedia = ifElse(
    Array.isArray,
    head,
    pipe(
        prop('media'),
        last,
    ),
);

/**
 * @param {function(String, String, String): *} messageHandler
 * @returns {function(Backbone.Collection, String, Function): Promise}
 */
export const uploadToCollection = messageHandler => (collection, target, onFileComplete = () => {}) => {
    const files = pullFilesFromInput(target);
    return Promise.all(
        files.map(file =>
            collection
                .upload(file)
                // Depending on our upload path we either get the updated package or the media item. For now,
                // we're only going to return the media item and the file because none of the existing code
                // uses the whole model. If more than two cases exist this should be converted to a strategy
                // from the caller - ADM 4.28.17
                .then(mediaOrModelToMedia)
                .then(
                    tap(media => {
                        const ready = media.mediaStatus === 'READY';
                        messageHandler(
                            ready ? 'success' : 'info',
                            file.name,
                            ready ? 'successfully uploaded' : 'in queue for processing',
                        );
                    }),
                )
                .catch((err = {}) => {
                    const errDetail = err.message ? ` (${err.message})` : '';
                    messageHandler('error', file.name, `failed to upload${errDetail}`);
                })
                .then(tap(media => onFileComplete({media, file}))),
        ),
    ).then(reduce(maxBy(pathOr(0, ['media', 'length'])), {}));
};

const buildDataFromFile = pipe(
    getFileData,
    tap(filedata => (filedata.caption = captionFilter(filedata.caption))),
);

export default Collection.extend(
    Object.assign({}, collectionMixins.search, {
        url: '/platform/media/',
        model: MediaModel,
        initialize(models) {
            bindAllMethods(this);

            this.searchInitialize();
            // Search default starting type
            this.setPackageTypes(['IMAGE', 'VIDEO', 'AUDIO']);
            this.setSearchType('media');

            this.add(models);
            this._pendingUploadData = [];
        },
        parse(models) {
            this.add(models);

            return models;
        },
        removeDataFromPending(filedata) {
            this._pendingUploadData = this._pendingUploadData.filter(pending => pending !== filedata);
        },
        _isValid(file) {
            return new Promise((resolve, reject) => {
                const MAX_WIDTH = 1920;
                const MAX_HEIGHT = 1080;
                const MIN_DURATION = 5;

                // Note, all images should have file.type as 'image/*'. IE, 'image/jpeg'
                // Source, https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types
                if (file.type.startsWith('image')) {
                    resolve(file);
                    return;
                }

                function processUpload(video) {
                    const {videoHeight, videoWidth, duration} = video;
                    const errs = [];


                    // TO DO: fix this logic based on backendend requirement for video sizing
                    if (videoWidth * videoHeight > MAX_WIDTH * MAX_HEIGHT) {
                        errs.push('1080p max resolution');
                    }

                    if (duration < MIN_DURATION) {
                        errs.push(`Minimum ${MIN_DURATION}s duration`);
                    }

                    errs.length !== 0 ? reject(new Error(errs.join('; '))) : resolve(file);
                }

                const video = document.createElement('video');
                video.preload = 'metadata';

                video.onloadedmetadata = function() {
                    window.URL.revokeObjectURL(video.src);
                    processUpload(video);
                };

                video.src = URL.createObjectURL(file);
            });
        },
        upload(file) {
            return this._isValid(file)
                .then(validFile => buildDataFromFile(validFile))
                .then(filedata => {
                    // Add metadata to pending uploads
                    this._pendingUploadData.push(filedata);

                    return (
                        uploadFileWithData(this.url, file, filedata)
                            // TODO: This should be a finally - ADM 4.26.17
                            .then(tap(() => this.removeDataFromPending(filedata)))
                            .catch(err => {
                                this.removeDataFromPending(filedata);
                                return Promise.reject(err);
                            })
                    );
                });
        },
        getPendingUploads() {
            return createImmutableCopy(this._pendingUploadData);
        },
        _modelJSONIsImage(modelData) {
            return modelData.mediaType === 'image';
        },
        _modelJSONIsVideo(modelData) {
            return modelData.mediaType === 'video' || modelData.mediaType === 'audio';
        },
        _fileExists(json, filedata) {
            return _.find(json.media, _.omit(filedata, 'filename'));
        },
        getAllFileMetadata(files) {
            return Promise.all(Array.from(files).map(buildDataFromFile));
        },
        _createModelFromJSON(model) {
            if (this._modelJSONIsImage(model)) {
                return new ImageModel(model);
            }
            if (this._modelJSONIsVideo(model)) {
                return new VideoModel(model);
            }

            return new MediaModel(model);
        },
        add(coll) {
            let models = coll;
            if (Array.isArray(models)) {
                models = models.map(this._createModelFromJSON);
            } else if (models && !this._isModel(models)) {
                models = this._createModelFromJSON(models);
            }

            return Collection.prototype.add.call(this, models);
        },
        getImageCount() {
            return this.getImages().length;
        },
        getImages() {
            return this._getAll('image');
        },
        getVideos() {
            return this._getAll('video');
        },
        getMediasByTypes(mediaTypes) {
            return this.toJSON().filter(media => mediaTypes.some(type => media.mediaType === type));
        },
        _getAll(type) {
            return this.where({mediaType: type}).map(model => model.toJSON());
        },
        // Get mediaItem that matches passed external id then shove in a `Maybe`
        // getPreviewMediaItem :: UUID -> Maybe MediaItem
        getPreviewMediaItem(externalId) {
            return fromNullable(this.findWhere({externalId}));
        },
        // getPreviewUrlByExternalId :: ExternalId -> Maybe URL
        getPreviewUrlByExternalId(renditionName, externalId) {
            return this.getPreviewMediaItem(externalId).chain(mediaItem => {
                if (propEq('mediaType', 'video')(mediaItem)) {
                    return fromNullable(mediaItem.get('thumbUrl'));
                } else {
                    const rend = mediaItem.getRendition(renditionName);
                    return rend ? Just(rend.get('url')) : Nothing();
                }
            });
        },
    }),
);
